import { useCallback, useEffect, useState } from 'react';
import {
  useGetReplacementsUserListQuery,
  useGetReplacementUserByEmpNoQuery,
} from '@Store/api/userApiSlice';
import { UserReplacement } from '@Types/index';
import useDebounce from '@Hooks/useDebounce';

interface ReturnValue {
  replacementUsers: UserReplacement[] | undefined;
  replacementEmployee: UserReplacement | undefined;
  onLoadMore: () => void;
  onSearch: (searchValue: string) => void;
}

interface UseReplacementManagerProps {
  skipGetReplacementUsers: boolean;
  replacementEmpNo?: string;
}

const useReplacementManager = ({
  skipGetReplacementUsers,
  replacementEmpNo,
}: UseReplacementManagerProps): ReturnValue => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(9);
  const [searchValue, setSearchValue] = useState('');
  const searchValueQuery = useDebounce(searchValue, 300);
  const { data: replacementEmployee } = useGetReplacementUserByEmpNoQuery(
    replacementEmpNo || '',
    {
      skip: !replacementEmpNo,
      refetchOnMountOrArgChange: true,
    }
  );
  const { data: replacementUsers } = useGetReplacementsUserListQuery(
    {
      page: currentPage,
      pageSize,
      searchValue: String(searchValueQuery),
    },
    { skip: skipGetReplacementUsers, refetchOnMountOrArgChange: true }
  );
  const [loadedReplacementUsers, setLoadedReplacementUsers] = useState<
    UserReplacement[]
  >([]);

  const handleLoadMore = (): void => {
    setPageSize(9);
    setCurrentPage(currentPage + 1);
  };

  useEffect(() => {
    if (replacementUsers) {
      setLoadedReplacementUsers([
        ...loadedReplacementUsers,
        ...replacementUsers,
      ]);
    }
  }, [replacementUsers]);

  const handleSearch = useCallback((searchQuery: string): void => {
    setLoadedReplacementUsers([]);
    setSearchValue(searchQuery);
    setPageSize(20);
  }, []);

  return {
    replacementUsers: loadedReplacementUsers.length
      ? loadedReplacementUsers
      : replacementUsers,
    replacementEmployee,
    onLoadMore: handleLoadMore,
    onSearch: handleSearch,
  };
};

export default useReplacementManager;
